import React from 'react';
import {
  Bar,
  BarChart,
  Line,
  LineChart,
  Pie,
  PieChart,
  Area,
  AreaChart,
  Scatter,
  ScatterChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ZAxis,
  Tooltip,
  Legend,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import type { ChartSpec } from '@/utils/chartParser';

interface OrgainChartProps {
  spec: ChartSpec;
}

/**
 * Palette used to rotate per-series colours when the LLM doesn't supply
 * explicit `color` overrides. Picked to read well on both light and dark
 * message bubbles.
 */
const DEFAULT_PALETTE = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#14b8a6', // teal-500
  '#f97316', // orange-500
];

function colorFor(
  series: NonNullable<ChartSpec['series']>[number] | undefined,
  index: number,
): string {
  return series?.color || DEFAULT_PALETTE[index % DEFAULT_PALETTE.length];
}

/**
 * Renders an `orgain-chart` ChartSpec via recharts. Each chart `type` selects
 * the matching recharts chart component. Renders an optional title above the
 * chart and uses a ResponsiveContainer so the chart fills its column.
 *
 * Robustness: if `spec.series` is missing/empty, we infer a single series from
 * the first non-xKey property of the first data row. Unknown chart types fall
 * back to a small inline warning rather than throwing.
 */
export const OrgainChart: React.FC<OrgainChartProps> = ({ spec }) => {
  const { type, title, xKey, data } = spec;
  const safeData = Array.isArray(data) ? data : [];

  // Resolve series - fall back to inferring from the first data row's keys
  // (excluding xKey) when the LLM omits the `series` array.
  let series = spec.series && spec.series.length > 0 ? spec.series : [];
  if (series.length === 0 && safeData.length > 0) {
    const firstRow = safeData[0] || {};
    const inferredKeys = Object.keys(firstRow).filter((k) => k !== xKey);
    series = inferredKeys.map((dataKey) => ({ dataKey }));
  }

  const height = 360;

  const Title = title ? (
    <div className="text-sm font-semibold mb-2 text-center opacity-90">{title}</div>
  ) : null;

  const wrapperClass =
    'my-3 rounded-lg border border-white/10 bg-white/[0.03] p-3 max-w-full overflow-hidden';

  // Pie charts have a different shape from cartesian charts.
  if (type === 'pie') {
    const primarySeries = series[0];
    const valueKey = primarySeries?.dataKey;
    if (!valueKey || safeData.length === 0) {
      return (
        <div className={wrapperClass}>
          {Title}
          <div className="text-xs opacity-70">No pie data to display.</div>
        </div>
      );
    }
    return (
      <div className={wrapperClass}>
        {Title}
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Tooltip />
            <Legend />
            <Pie
              data={safeData}
              dataKey={valueKey}
              nameKey={xKey || 'name'}
              cx="50%"
              cy="50%"
              outerRadius={120}
              label
            >
              {safeData.map((_, i) => (
                <Cell key={`cell-${i}`} fill={DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Cartesian charts share the same axes + grid + tooltip + legend chrome.
  const renderCartesianBody = () => {
    if (type === 'bar') {
      return (
        <BarChart data={safeData} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          {series.map((s, i) => (
            <Bar
              key={s.dataKey}
              dataKey={s.dataKey}
              name={s.name || s.dataKey}
              fill={colorFor(s, i)}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      );
    }
    if (type === 'line') {
      return (
        <LineChart data={safeData} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          {series.map((s, i) => (
            <Line
              key={s.dataKey}
              type="monotone"
              dataKey={s.dataKey}
              name={s.name || s.dataKey}
              stroke={colorFor(s, i)}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          ))}
        </LineChart>
      );
    }
    if (type === 'area') {
      return (
        <AreaChart data={safeData} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
          <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend />
          {series.map((s, i) => {
            const c = colorFor(s, i);
            return (
              <Area
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.name || s.dataKey}
                stroke={c}
                fill={c}
                fillOpacity={0.25}
                strokeWidth={2}
              />
            );
          })}
        </AreaChart>
      );
    }
    if (type === 'scatter') {
      // For scatter, expect each series to use xKey for x and its dataKey for y.
      return (
        <ScatterChart margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
          <XAxis dataKey={xKey} type="category" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <ZAxis range={[60, 60]} />
          <Tooltip cursor={{ strokeDasharray: '3 3' }} />
          <Legend />
          {series.map((s, i) => (
            <Scatter
              key={s.dataKey}
              name={s.name || s.dataKey}
              data={safeData.map((row) => ({ ...row, [s.dataKey]: row[s.dataKey] }))}
              dataKey={s.dataKey}
              fill={colorFor(s, i)}
            />
          ))}
        </ScatterChart>
      );
    }
    return null;
  };

  const body = renderCartesianBody();
  if (!body) {
    return (
      <div className={wrapperClass}>
        {Title}
        <div className="text-xs opacity-70">Unsupported chart type: {String(type)}</div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      {Title}
      <ResponsiveContainer width="100%" height={height}>
        {body}
      </ResponsiveContainer>
    </div>
  );
};

export default OrgainChart;
